import { AIModule } from '../types';

// AI Agent Development Masterclass modules
export const AI_AGENT_INTRO_MODULE: AIModule = {
  id: 'ai-agent-intro',
  title: 'Introduction to AI Agents & Agent Architecture',
  description: 'Learn the fundamentals of AI agents, their architecture, and core components',
  duration: '4 hours',
  difficulty: 'intermediate',
  content: `
# Introduction to AI Agents & Agent Architecture

## What are AI Agents?

AI agents are autonomous systems that can perceive their environment, make decisions, and take actions to achieve specific goals. Unlike traditional software that follows predefined workflows, AI agents can adapt, learn, and reason about their tasks.

## Core Components of AI Agents

### 1. Perception System
- Environment sensing capabilities
- Input processing and interpretation
- Multi-modal data integration

### 2. Decision Making Engine
- Goal-oriented reasoning
- Planning and strategy selection
- Context-aware decision making

### 3. Action Execution
- Tool utilization capabilities
- API integration and communication
- Environment manipulation

### 4. Memory Systems
- Short-term working memory
- Long-term knowledge storage
- Experience-based learning

## Agent Architecture Patterns

### Reactive Agents
Simple stimulus-response patterns for immediate reactions.

### Deliberative Agents
Planning-based agents that think before acting.

### Hybrid Agents
Combining reactive and deliberative approaches for optimal performance.

## Real-World Applications

- Customer service chatbots
- Personal assistants
- Automated trading systems
- Content creation agents
- Code generation assistants
  `,
  objectives: [
    'Understand the core concepts of AI agents',
    'Learn different agent architectures',
    'Identify components of intelligent systems',
    'Recognize real-world applications'
  ],
  codeExamples: [
    {
      id: 'simple-agent',
      title: 'Simple Agent Structure',
      description: 'Basic agent implementation pattern',
      code: `
class SimpleAgent:
    def __init__(self, name, goal):
        self.name = name
        self.goal = goal
        self.memory = {}
        self.tools = []
    
    def perceive(self, environment):
        """Process environmental inputs"""
        return environment.get_current_state()
    
    def decide(self, perception):
        """Make decisions based on perception"""
        if self.goal in perception:
            return "achieve_goal"
        return "explore"
    
    def act(self, action, environment):
        """Execute actions in environment"""
        return environment.execute(action)
    
    def run_cycle(self, environment):
        perception = self.perceive(environment)
        action = self.decide(perception)
        result = self.act(action, environment)
        return result
      `,
      language: 'python',
      category: 'demo'
    }
  ],
  quiz: {
    questions: [
      {
        id: 'agent-components',
        question: 'Which component is responsible for interpreting environmental data in an AI agent?',
        options: [
          'Action Execution',
          'Perception System',
          'Memory System',
          'Decision Engine'
        ],
        correctAnswer: 1,
        explanation: 'The Perception System handles sensing and interpreting environmental data.'
      }
    ]
  },
  practice: {
    task: 'Design a simple agent architecture for a task management assistant',
    starter: 'Define the key components and their interactions',
    hints: [
      'Consider what inputs the agent needs to perceive',
      'Think about the decision-making process',
      'Define what actions the agent can take'
    ]
  }
};

export const LANGCHAIN_ORCHESTRATION_MODULE: AIModule = {
  id: 'langchain-orchestration',
  title: 'LangChain Framework & Agent Orchestration',
  description: 'Master LangChain for building and orchestrating complex AI agents',
  duration: '5 hours',
  difficulty: 'intermediate',
  content: `
# LangChain Framework & Agent Orchestration

## Introduction to LangChain

LangChain is a powerful framework for developing applications with large language models. It provides abstractions and tools for building complex AI workflows.

## Core LangChain Concepts

### Chains
Sequences of calls to LLMs or other utilities.

### Agents
Systems that use LLMs to determine which actions to take.

### Tools
Functions that agents can use to interact with the world.

### Memory
Systems for persisting state between chain/agent calls.

## Building Your First LangChain Agent

### Setting Up the Environment
Install required packages and configure API keys.

### Creating Tools
Define functions your agent can use.

### Agent Initialization
Set up the agent with tools and memory.

### Execution and Monitoring
Run the agent and track its performance.

## Advanced Orchestration Patterns

### Multi-Step Reasoning
Breaking down complex tasks into manageable steps.

### Tool Selection Strategy
How agents choose the right tool for each task.

### Error Handling and Recovery
Managing failures and retries in agent workflows.
  `,
  objectives: [
    'Set up and configure LangChain',
    'Create custom tools for agents',
    'Build multi-step agent workflows',
    'Implement error handling strategies'
  ],
  codeExamples: [
    {
      id: 'langchain-agent',
      title: 'Basic LangChain Agent',
      description: 'Creating a simple agent with tools',
      code: `
from langchain.agents import initialize_agent, Tool
from langchain.llms import OpenAI
from langchain.memory import ConversationBufferMemory

# Define custom tools
def search_tool(query):
    """Search for information"""
    # Implementation here
    return f"Search results for: {query}"

def calculator_tool(expression):
    """Calculate mathematical expressions"""
    try:
        result = eval(expression)
        return f"Result: {result}"
    except:
        return "Invalid expression"

# Initialize tools
tools = [
    Tool(
        name="Search",
        func=search_tool,
        description="Useful for finding information"
    ),
    Tool(
        name="Calculator",
        func=calculator_tool,
        description="Useful for mathematical calculations"
    )
]

# Initialize LLM and memory
llm = OpenAI(temperature=0)
memory = ConversationBufferMemory(memory_key="chat_history")

# Create agent
agent = initialize_agent(
    tools=tools,
    llm=llm,
    agent="conversational-react-description",
    memory=memory,
    verbose=True
)

# Run agent
response = agent.run("What is 15 * 24 and then search for Python tutorials")
print(response)
      `,
      language: 'python',
      category: 'tutorial'
    }
  ],
  quiz: {
    questions: [
      {
        id: 'langchain-concepts',
        question: 'What is the primary purpose of Tools in LangChain?',
        options: [
          'Store conversation history',
          'Provide functions that agents can use',
          'Configure the LLM parameters',
          'Handle error messages'
        ],
        correctAnswer: 1,
        explanation: 'Tools provide specific functions that agents can use to interact with external systems or perform tasks.'
      }
    ]
  }
};

export const MULTI_AGENT_SYSTEMS_MODULE: AIModule = {
  id: 'multi-agent-systems',
  title: 'Multi-Agent Systems & Agent Communication',
  description: 'Build systems with multiple AI agents that collaborate and communicate',
  duration: '5 hours',
  difficulty: 'advanced',
  content: `
# Multi-Agent Systems & Agent Communication

## What are Multi-Agent Systems?

Multi-agent systems consist of multiple autonomous agents that interact and collaborate to solve complex problems that would be difficult for a single agent.

## Agent Communication Protocols

### Message Passing
- Asynchronous communication between agents
- Message queues and event-driven architecture
- Protocol standards (FIPA-ACL, KQML)

### Shared Memory
- Common data stores for agent coordination
- Distributed databases and caching systems
- Conflict resolution mechanisms

### Negotiation Protocols
- Auction-based task allocation
- Contract net protocols
- Consensus mechanisms

## Coordination Strategies

### Hierarchical Organization
Master-slave relationships with clear authority chains.

### Peer-to-Peer Networks
Distributed decision making without central authority.

### Market-Based Coordination
Economic models for task allocation and resource sharing.

## Implementation Patterns

### CrewAI Framework
Modern framework for orchestrating role-based agent teams.

### AutoGen Framework
Microsoft's framework for conversational AI agents.

### Custom Orchestration
Building your own coordination layer.
  `,
  objectives: [
    'Design multi-agent system architectures',
    'Implement agent communication protocols',
    'Build coordination mechanisms',
    'Use frameworks like CrewAI and AutoGen'
  ],
  codeExamples: [
    {
      id: 'crewai-example',
      title: 'CrewAI Multi-Agent System',
      description: 'Creating a team of specialized agents',
      code: `
from crewai import Agent, Task, Crew
from langchain.llms import OpenAI

# Initialize LLM
llm = OpenAI(temperature=0.7)

# Define specialized agents
researcher = Agent(
    role='Research Specialist',
    goal='Gather comprehensive information on given topics',
    backstory='Expert at finding and analyzing information from various sources',
    llm=llm,
    verbose=True
)

writer = Agent(
    role='Content Writer',
    goal='Create engaging and informative content',
    backstory='Skilled writer who can transform research into compelling narratives',
    llm=llm,
    verbose=True
)

editor = Agent(
    role='Content Editor',
    goal='Review and improve written content',
    backstory='Meticulous editor with an eye for detail and clarity',
    llm=llm,
    verbose=True
)

# Define tasks
research_task = Task(
    description='Research the latest trends in AI agent development',
    agent=researcher
)

writing_task = Task(
    description='Write an article based on the research findings',
    agent=writer
)

editing_task = Task(
    description='Edit and polish the written article',
    agent=editor
)

# Create crew
crew = Crew(
    agents=[researcher, writer, editor],
    tasks=[research_task, writing_task, editing_task],
    verbose=True
)

# Execute the workflow
result = crew.kickoff()
print(result)
      `,
      language: 'python',
      category: 'tutorial'
    }
  ]
};

export const TOOL_USING_AGENTS_MODULE: AIModule = {
  id: 'tool-using-agents',
  title: 'Tool-Using Agents & API Integration',
  description: 'Create agents that can use external tools and integrate with APIs',
  duration: '4 hours',
  difficulty: 'intermediate',
  content: `
# Tool-Using Agents & API Integration

## Understanding Tool Usage in AI Agents

Tool-using agents extend beyond language generation to interact with external systems, APIs, and services, making them capable of performing real-world tasks.

## Types of Agent Tools

### Information Retrieval Tools
- Web search engines
- Database queries
- Document retrieval systems
- Knowledge base searches

### Communication Tools
- Email clients
- Messaging APIs
- Social media integrations
- Notification systems

### Computational Tools
- Calculators and math engines
- Data analysis libraries
- Image processing tools
- Code execution environments

### External Service APIs
- Payment processors
- CRM systems
- Cloud services
- Third-party integrations

## Tool Integration Patterns

### Function Calling
Modern LLMs can determine when and how to call functions based on natural language instructions.

### API Wrapper Creation
Building standardized interfaces for external services.

### Error Handling and Retries
Managing failures and implementing robust error recovery.

### Rate Limiting and Throttling
Respecting API limits and managing request flows.
  `,
  objectives: [
    'Implement various types of agent tools',
    'Integrate external APIs effectively',
    'Handle errors and edge cases',
    'Build robust tool interfaces'
  ],
  codeExamples: [
    {
      id: 'api-tool',
      title: 'API Integration Tool',
      description: 'Creating a tool that integrates with external APIs',
      code: `
import requests
from typing import Dict, Any
import json

class APITool:
    def __init__(self, base_url: str, api_key: str = None):
        self.base_url = base_url
        self.api_key = api_key
        self.session = requests.Session()
        
        if api_key:
            self.session.headers.update({
                'Authorization': f'Bearer {api_key}',
                'Content-Type': 'application/json'
            })
    
    def make_request(self, method: str, endpoint: str, **kwargs) -> Dict[str, Any]:
        """Make HTTP request with error handling"""
        url = f"{self.base_url}/{endpoint.lstrip('/')}"
        
        try:
            response = self.session.request(method, url, **kwargs)
            response.raise_for_status()
            return {
                'success': True,
                'data': response.json(),
                'status_code': response.status_code
            }
        except requests.exceptions.RequestException as e:
            return {
                'success': False,
                'error': str(e),
                'status_code': getattr(e.response, 'status_code', None)
            }
    
    def search_weather(self, city: str) -> str:
        """Tool function for weather search"""
        result = self.make_request(
            'GET', 
            f'weather/current',
            params={'city': city}
        )
        
        if result['success']:
            weather_data = result['data']
            return f"Weather in {city}: {weather_data.get('description', 'N/A')}, {weather_data.get('temperature', 'N/A')}°C"
        else:
            return f"Failed to get weather for {city}: {result['error']}"

# Usage with LangChain
from langchain.tools import Tool

weather_api = APITool('https://api.weather.com', 'your-api-key')

weather_tool = Tool(
    name="Weather Search",
    func=weather_api.search_weather,
    description="Get current weather information for a city"
)
      `,
      language: 'python',
      category: 'tutorial'
    }
  ]
};

export const AGENT_MEMORY_MODULE: AIModule = {
  id: 'agent-memory',
  title: 'Memory Systems & Agent State Management',
  description: 'Implement sophisticated memory systems for persistent agent behavior',
  duration: '3 hours',
  difficulty: 'intermediate',
  content: `
# Memory Systems & Agent State Management

## Types of Agent Memory

### Short-Term Memory
- Working memory for current context
- Conversation buffers
- Temporary state storage

### Long-Term Memory
- Persistent knowledge storage
- Learning from experiences
- Skill and fact retention

### Episodic Memory
- Specific event recollection
- Contextual information storage
- Time-based memory organization

### Semantic Memory
- General knowledge and concepts
- Relationship understanding
- Abstract reasoning capabilities

## Memory Implementation Strategies

### Vector Databases
Using embeddings for semantic memory storage and retrieval.

### Graph Databases
Storing relationships and complex associations.

### Traditional Databases
Structured data storage for facts and events.

### Hybrid Approaches
Combining multiple memory types for optimal performance.

## State Management Patterns

### Centralized State
Single source of truth for agent state.

### Distributed State
Partitioned state across multiple systems.

### Event Sourcing
State reconstruction from event history.
  `,
  objectives: [
    'Design effective memory architectures',
    'Implement different memory types',
    'Manage agent state persistence',
    'Build memory retrieval systems'
  ]
};

export const CONVERSATIONAL_AI_MODULE: AIModule = {
  id: 'conversational-ai',
  title: 'Conversational AI Agents & Voice Integration',
  description: 'Build sophisticated conversational agents with voice capabilities',
  duration: '4 hours',
  difficulty: 'intermediate',
  content: `
# Conversational AI Agents & Voice Integration

## Conversational AI Fundamentals

### Dialog Management
- Turn-taking protocols
- Context maintenance
- Intent recognition and slot filling
- Multi-turn conversation handling

### Natural Language Understanding (NLU)
- Intent classification
- Entity extraction
- Sentiment analysis
- Context interpretation

### Natural Language Generation (NLG)
- Response generation strategies
- Personality and tone management
- Dynamic content creation
- Template-based vs. generative approaches

## Voice Integration Technologies

### Speech-to-Text (STT)
- Real-time transcription
- Multi-language support
- Noise handling and preprocessing
- Accuracy optimization techniques

### Text-to-Speech (TTS)
- Natural voice synthesis
- Emotion and intonation control
- Custom voice creation
- Real-time audio generation

### Voice Activity Detection (VAD)
- Turn detection in conversations
- Silence handling
- Background noise filtering
- Interruption management

## Building Voice-Enabled Agents

### Architecture Design
- Audio pipeline management
- Real-time processing requirements
- Latency optimization
- Error handling and recovery

### Integration Patterns
- WebRTC for browser-based voice
- Telephony system integration
- Mobile app voice interfaces
- Smart speaker development
  `,
  objectives: [
    'Build conversational AI systems',
    'Integrate speech-to-text and text-to-speech',
    'Handle multi-turn conversations',
    'Deploy voice-enabled applications'
  ]
};

export const ADVANCED_AGENT_PATTERNS_MODULE: AIModule = {
  id: 'advanced-agent-patterns',
  title: 'Advanced Agent Patterns (AutoGPT, ReAct, etc.)',
  description: 'Implement cutting-edge agent architectures and reasoning patterns',
  duration: '4 hours',
  difficulty: 'advanced',
  content: `
# Advanced Agent Patterns

## AutoGPT Pattern

AutoGPT represents a breakthrough in autonomous AI agents that can break down complex goals into subtasks and execute them independently.

### Core Components
- Goal decomposition algorithms
- Task prioritization systems
- Self-evaluation mechanisms
- Autonomous execution loops

### Implementation Strategy
- Memory management for long-term goals
- Tool selection and usage
- Progress tracking and adjustment
- Human intervention protocols

## ReAct (Reasoning + Acting) Pattern

ReAct combines reasoning traces with action execution, allowing agents to think through problems step-by-step while taking actions.

### Reasoning Traces
- Step-by-step thought processes
- Problem decomposition
- Evidence evaluation
- Decision justification

### Action Integration
- Interleaving thoughts with actions
- Observation-based reasoning
- Dynamic strategy adjustment
- Error correction loops

## Tree of Thoughts Pattern

An extension of chain-of-thought reasoning that explores multiple reasoning paths simultaneously.

### Branching Strategies
- Multiple hypothesis generation
- Parallel reasoning paths
- Evaluation and pruning
- Best path selection

### Search Algorithms
- Breadth-first exploration
- Depth-first investigation
- Best-first search strategies
- Heuristic-guided exploration

## Agent Planning Architectures

### Hierarchical Task Networks (HTN)
- Decomposing complex tasks
- Method selection strategies
- Constraint satisfaction
- Plan optimization

### STRIPS-style Planning
- State-space representation
- Goal-oriented planning
- Precondition evaluation
- Effect prediction

## Modern Agent Frameworks

### LangGraph
- Graph-based agent workflows
- State management
- Conditional execution paths
- Human-in-the-loop integration

### Semantic Kernel
- Microsoft's agent framework
- Plugin-based architecture
- Memory and planning integration
- Enterprise-ready features
  `,
  objectives: [
    'Implement AutoGPT-style autonomous agents',
    'Build ReAct reasoning patterns',
    'Create Tree of Thoughts systems',
    'Use modern agent frameworks'
  ]
};